import { SharedAuthEvent } from '@loomhq/enums';
import {
  decode,
  encode,
  generateRSAKeys,
  privateDecrypt,
} from '@loomhq/shared-auth-browser';
import uuid from 'uuid';

import * as analytics from '../analytics';
import * as loggerx from '../loggerx';
import onSessionRequestToken from '../../creators/onSessionRequestToken.graphql';
import { getGraphQLClient } from '../graphql';
import { LOOM_URI } from '../../constants/routes';
import { APP_SOURCE_SLACK_DESKTOP } from '../../../../shared/constants/analytics';

const SESSION_REQUEST_CALLBACK_FAILED = 'failure';

export const invokeSharedAuthFlow = async (
  videoId,
  entryPoint,
  isSlackSharedAuthV2
) => {
  const loginAttemptId = uuid.v4();
  const trackingIds = analytics.getAnalyticsIds();

  const track = eventName => {
    analytics.track(eventName, {
      source: APP_SOURCE_SLACK_DESKTOP,
      loginAttemptId,
      anonymousId: trackingIds.anonID,
      deviceId: trackingIds.deviceID,
      entryPoint,
      isSlackSharedAuthV2,
    });
  };

  track(SharedAuthEvent.Initiated);

  try {
    const { publicKeyAsPem, privateKey } = await generateRSAKeys();
    const encodedPublicKey = encode(publicKeyAsPem);

    const gqlClient = getGraphQLClient();
    const subscription = gqlClient
      .subscribe({
        variables: { encodedPublicKey },
        query: onSessionRequestToken,
      })
      .subscribe(body => {
        subscription.unsubscribe();
        gqlClient.stop();

        sessionRequestCallback({
          body,
          trackingIds,
          loginAttemptId,
          privateKey,
          track,
        });
      });

    track(SharedAuthEvent.GraphQLSubscribed);

    const queryParams = {
      anon_id: trackingIds.anonID,
      device_id: trackingIds.deviceID,
      app_source: APP_SOURCE_SLACK_DESKTOP,
      ak: encodedPublicKey,
      login_attempt_id: loginAttemptId,
      custom_title: isSlackSharedAuthV2
        ? 'Sign in to Loom to connect your account'
        : 'Sign in',
      redirect_after: isSlackSharedAuthV2
        ? `${LOOM_URI}/slack/shared-auth/success`
        : `${LOOM_URI}/share/${videoId}`,
    };
    const queryParamStrings = Object.keys(queryParams).map(
      key => `${key}=${queryParams[key]}`
    );
    const url = `${LOOM_URI}/shared-auth/login?${queryParamStrings.join('&')}`;

    window.open(url);
  } catch (error) {
    loggerx.error(error, {
      message: 'Failed to invoke Slack shared auth flow',
    });
  }
};

const sessionRequestCallback = async ({
  body,
  trackingIds,
  loginAttemptId,
  privateKey,
  track,
}) => {
  track(SharedAuthEvent.GraphQLHeardBack);

  const { token: encryptedEncodedSRT, status } = body.data.sessionRequestToken;

  if (status === SESSION_REQUEST_CALLBACK_FAILED) {
    loggerx.error(Error('Session request callback failed.'), {
      message: `Server response: ${status}`,
    });

    return;
  }

  track(SharedAuthEvent.SessionRequestTokenReceived);

  const decryptedSRT = await privateDecrypt(
    privateKey,
    decode(encryptedEncodedSRT)
  );

  const queryParams = {
    anon_id: trackingIds.anonID,
    device_id: trackingIds.deviceID,
    login_attempt_id: loginAttemptId,
  };
  const queryParamStrings = Object.keys(queryParams).map(
    key => `${key}=${queryParams[key]}`
  );

  fetch(`${LOOM_URI}/api/auth/session?${queryParamStrings.join('&')}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      srt: encode(decryptedSRT),
    }),
  }).then(resp => {
    if (resp.status >= 400) {
      loggerx.error(Error('Fetch session request token failed.'), {
        message: `Response: [${resp.status}] ${resp.message}`,
      });

      return;
    }

    track(SharedAuthEvent.LoginSuccess);

    window.location.reload();
  });
};
